/*
  Good Day components
  Small custom elements used by the redesigned homepage sections. They build on the
  theme's own helpers (theme.Slideshow wraps Flickity from vendor-scripts) so no extra
  slider library is loaded. Each element cleans up after itself when removed, which
  keeps the Shopify theme editor happy when sections reload.

  <gd-carousel>  Flickity carousel. Options come from data-options (JSON). Any button with
                 [data-carousel-prev] / [data-carousel-next] and aria-controls="<carousel id>"
                 drives it (delegated, so aria-controls can change at runtime).
                 data-media="(max-width: 768px)" keeps it active only while that matches.
  <gd-marquee>   Infinite auto-scrolling row. The child [data-marquee-track] holds one
                 [data-marquee-group] of cards; the element clones the group to fill the
                 loop and sets --marquee-distance / --marquee-duration (data-speed = px/s).
  <gd-tabs>      Accessible tabs (role="tab" / role="tabpanel"). A [data-tabs-nav] in the
                 same section is pointed at the active panel's carousel.
  <gd-product-card> Strap/colour swatches swap the card image for the variant image.
*/
(function () {
  "use strict";

  if (!("customElements" in window)) return;
  // Several sections include this file; run it once.
  if (window.gdComponentsLoaded) return;
  window.gdComponentsLoaded = true;

  function sectionOf(el) {
    return el.closest("[data-section-id]") || document;
  }

  document.addEventListener("click", function (evt) {
    const btn = evt.target.closest("[data-carousel-prev], [data-carousel-next]");
    if (!btn) return;
    const target = document.getElementById(btn.getAttribute("aria-controls") || "");
    if (!target || !target.flickity) return;
    evt.preventDefault();
    if (btn.hasAttribute("data-carousel-prev")) target.flickity.previous();
    else target.flickity.next();
  });

  /* -------------------------------------------------------------------------- */
  /* <gd-carousel>                                                              */
  /* -------------------------------------------------------------------------- */

  class GDCarousel extends HTMLElement {
    constructor() {
      super();
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
    }

    connectedCallback() {
      if (this.slider) return;

      this.sectionId = (sectionOf(this).dataset || {}).sectionId;
      if (this.dataset.media && window.matchMedia) {
        this.media = window.matchMedia(this.dataset.media);
        if (this.media.addEventListener) {
          this.media.addEventListener("change", this.onMediaChange);
        } else if (this.media.addListener) {
          this.media.addListener(this.onMediaChange);
        }
      }
      document.addEventListener("shopify:block:select", this.onBlockSelect);

      const start = () => {
        if (!window.theme || !theme.Slideshow) return;
        if (theme.initWhenVisible) {
          theme.initWhenVisible({
            element: this,
            callback: this.init.bind(this),
            threshold: 600,
          });
        } else {
          this.init();
        }
      };

      if (window.theme && theme.Slideshow) {
        start();
      } else {
        document.addEventListener("page:loaded", start, { once: true });
      }
    }

    disconnectedCallback() {
      document.removeEventListener("shopify:block:select", this.onBlockSelect);
      if (this.media) {
        if (this.media.removeEventListener) {
          this.media.removeEventListener("change", this.onMediaChange);
        } else if (this.media.removeListener) {
          this.media.removeListener(this.onMediaChange);
        }
      }

      this.destroy();
    }

    destroy() {
      if (this.cellObserver) {
        this.cellObserver.disconnect();
        this.cellObserver = null;
      }
      if (!this.slider) return;
      this.slider.destroy();
      this.slider = null;
      this.classList.remove("is-ready");
    }

    onMediaChange() {
      if (this.media.matches) {
        this.init();
      } else {
        this.destroy();
      }
    }

    // Called by whoever reveals a carousel that was hidden (tab panels).
    resize() {
      if (this.flickity) this.flickity.resize();
    }

    init() {
      if (this.slider || !this.isConnected) return;
      if (this.media && !this.media.matches) return;
      if (!window.theme || !theme.Slideshow) return;

      let options = {};
      try {
        options = JSON.parse(this.dataset.options || "{}");
      } catch (e) {
        options = {};
      }

      const args = Object.assign(
        {
          wrapAround: true,
          pageDots: false,
          prevNextButtons: false,
          cellAlign: "center",
          adaptiveHeight: false,
          contain: false,
        },
        options,
      );

      if (this.children.length < 2) {
        args.wrapAround = false;
        args.draggable = false;
        args.autoPlay = false;
      }

      // theme.Slideshow expects this wrapper when autoPlay is on.
      if (args.autoPlay && !this.closest(".slideshow-wrapper")) {
        this.parentElement.classList.add("slideshow-wrapper");
      }

      this.slider = new theme.Slideshow(this, args);
      this.classList.add("is-ready");
      this.resize();
      this.watchCells();
    }

    // Re-measure whenever a cell changes size (Flickity caches sizes at init).
    watchCells() {
      if (!("ResizeObserver" in window) || this.cellObserver) return;
      this.cellObserver = new ResizeObserver(() => {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = window.requestAnimationFrame(() => this.resize());
      });
      if (this.flickity) {
        this.flickity.cells.forEach((cell) =>
          this.cellObserver.observe(cell.element),
        );
      }
    }

    get flickity() {
      return this.slider ? this.slider.slideshow : null;
    }

    onBlockSelect(evt) {
      if (!this.flickity || evt.detail.sectionId !== this.sectionId) return;
      const cell = this.querySelector(
        '[data-block-id="' + evt.detail.blockId + '"]',
      );
      if (!cell) return;
      const index = Array.prototype.indexOf.call(
        this.flickity.cells.map((c) => c.element),
        cell,
      );
      if (index > -1) this.flickity.select(index);
    }
  }

  /* -------------------------------------------------------------------------- */
  /* <gd-marquee>                                                               */
  /* -------------------------------------------------------------------------- */

  class GDMarquee extends HTMLElement {
    constructor() {
      super();
      this.onResize = this.onResize.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
    }

    connectedCallback() {
      if (this.track) return;
      this.track = this.querySelector("[data-marquee-track]");
      this.group =
        this.track && this.track.querySelector("[data-marquee-group]");
      if (!this.track || !this.group || !this.group.children.length) return;

      this.sectionId = (sectionOf(this).dataset || {}).sectionId;
      this.speed = Number(this.dataset.speed) || 40; // px per second
      this.reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      if (this.reduceMotion) {
        this.classList.add("is-static");
        return;
      }

      this.build();
      window.addEventListener("resize", this.onResize);
      document.addEventListener("shopify:block:select", this.onBlockSelect);
      document.addEventListener("shopify:block:deselect", this.onBlockDeselect);
      this.observer = new IntersectionObserver(this.onVisibility, {
        rootMargin: "200px 0px",
      });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      window.removeEventListener("resize", this.onResize);
      document.removeEventListener("shopify:block:select", this.onBlockSelect);
      document.removeEventListener(
        "shopify:block:deselect",
        this.onBlockDeselect,
      );
      if (this.observer) this.observer.disconnect();
      if (this.resizeTimer) window.clearTimeout(this.resizeTimer);
      this.track = null;
    }

    // Clone the original group until the track is wider than two viewports, so the
    // loop never shows a gap, then animate by exactly one group width.
    build() {
      this.track
        .querySelectorAll("[data-marquee-clone]")
        .forEach((clone) => clone.remove());
      const groupWidth = this.group.getBoundingClientRect().width;
      if (!groupWidth) return;

      const needed = Math.max(
        2,
        Math.ceil((this.clientWidth * 2) / groupWidth) + 1,
      );
      for (let i = 1; i < needed; i++) {
        const clone = this.group.cloneNode(true);
        clone.setAttribute("data-marquee-clone", "");
        clone.setAttribute("aria-hidden", "true");
        clone
          .querySelectorAll("a, button, [tabindex]")
          .forEach((el) => el.setAttribute("tabindex", "-1"));
        this.track.appendChild(clone);
      }

      this.track.style.setProperty("--marquee-distance", `${groupWidth}px`);
      this.track.style.setProperty(
        "--marquee-duration",
        `${groupWidth / this.speed}s`,
      );
      this.classList.add("is-ready");
    }

    // Theme editor: hold the row still while one of its review blocks is selected so the
    // highlight stays on the card being edited.
    onBlockSelect(evt) {
      if (evt.detail.sectionId !== this.sectionId) return;
      this.classList.toggle("is-editing", this.contains(evt.target));
    }

    onBlockDeselect(evt) {
      if (evt.detail.sectionId !== this.sectionId) return;
      this.classList.remove("is-editing");
    }

    onResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => this.build(), 250);
    }

    onVisibility(entries) {
      entries.forEach((entry) =>
        this.classList.toggle("is-paused", !entry.isIntersecting),
      );
    }
  }

  /* -------------------------------------------------------------------------- */
  /* <gd-product-card>                                                          */
  /* -------------------------------------------------------------------------- */

  class GDProductCard extends HTMLElement {
    constructor() {
      super();
      this.onSwatchClick = this.onSwatchClick.bind(this);
    }

    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.addEventListener("click", this.onSwatchClick);
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.onSwatchClick);
      this.bound = false;
    }

    onSwatchClick(evt) {
      const button = evt.target.closest("[data-variant-url]");
      if (!button || !this.contains(button)) return;
      evt.preventDefault();

      const image = this.querySelector(".product-card__image");
      if (image && button.dataset.imageSrc) {
        image.src = button.dataset.imageSrc;
        image.srcset = button.dataset.imageSrcset || "";
        if (button.dataset.imageAlt) image.alt = button.dataset.imageAlt;
      }

      this.querySelectorAll(".product-card__swatch").forEach((item) => {
        const active = item.contains(button);
        item.classList.toggle("is-active", active);
        const control = item.querySelector("[data-variant-url]");
        if (control)
          control.setAttribute("aria-pressed", active ? "true" : "false");
      });

      this.querySelectorAll(
        ".product-card__media, .product-card__link",
      ).forEach((link) => {
        link.setAttribute("href", button.dataset.variantUrl);
      });
    }
  }

  /* -------------------------------------------------------------------------- */
  /* <gd-tabs>                                                                  */
  /* -------------------------------------------------------------------------- */

  class GDTabs extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
    }

    connectedCallback() {
      if (this.tabs) return;
      this.sectionId = (sectionOf(this).dataset || {}).sectionId;
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

      this.tabs.forEach((tab) => {
        tab.addEventListener("click", this.onClick);
        tab.addEventListener("keydown", this.onKeydown);
      });
      document.addEventListener("shopify:block:select", this.onBlockSelect);

      const initial = Math.max(
        0,
        this.tabs.findIndex(
          (tab) => tab.getAttribute("aria-selected") === "true",
        ),
      );
      this.select(initial, false);
    }

    disconnectedCallback() {
      if (!this.tabs) return;
      this.tabs.forEach((tab) => {
        tab.removeEventListener("click", this.onClick);
        tab.removeEventListener("keydown", this.onKeydown);
      });
      document.removeEventListener("shopify:block:select", this.onBlockSelect);
      this.tabs = null;
    }

    select(index, focus) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute("aria-selected", active ? "true" : "false");
        tab.tabIndex = active ? 0 : -1;
        if (active && focus) tab.focus();
      });
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
        if (i === index) {
          // Same frame as the reveal, so the panel never paints un-initialised.
          panel.querySelectorAll("gd-carousel").forEach((carousel) => {
            if (typeof carousel.init === "function") carousel.init();
            if (typeof carousel.resize === "function") carousel.resize();
          });
        }
      });
      this.updateNav(this.panels[index]);
      if (window.AOS) AOS.refresh();
    }

    // Point the shared prev/next buttons at the active panel's carousel; hide them when
    // that carousel has a single page.
    updateNav(panel) {
      const nav = sectionOf(this).querySelector("[data-tabs-nav]");
      if (!nav || !panel) return;
      const carousel = panel.querySelector("gd-carousel");
      const pages = carousel ? Number(carousel.dataset.pageCount) || 1 : 0;
      nav
        .querySelectorAll("[data-carousel-prev], [data-carousel-next]")
        .forEach((btn) => {
          if (carousel && carousel.id) btn.setAttribute("aria-controls", carousel.id);
        });
      nav.hidden = pages < 2;
    }

    onClick(evt) {
      this.select(this.tabs.indexOf(evt.currentTarget), false);
    }

    onKeydown(evt) {
      const current = this.tabs.indexOf(evt.currentTarget);
      let next = null;
      if (evt.key === "ArrowRight") next = (current + 1) % this.tabs.length;
      if (evt.key === "ArrowLeft")
        next = (current - 1 + this.tabs.length) % this.tabs.length;
      if (evt.key === "Home") next = 0;
      if (evt.key === "End") next = this.tabs.length - 1;
      if (next === null) return;
      evt.preventDefault();
      this.select(next, true);
    }

    onBlockSelect(evt) {
      if (evt.detail.sectionId !== this.sectionId) return;
      const index = this.tabs.findIndex(
        (tab) => tab.dataset.blockId === evt.detail.blockId,
      );
      if (index > -1) this.select(index, false);
    }
  }

  if (!customElements.get("gd-carousel"))
    customElements.define("gd-carousel", GDCarousel);
  if (!customElements.get("gd-tabs")) customElements.define("gd-tabs", GDTabs);
  if (!customElements.get("gd-product-card"))
    customElements.define("gd-product-card", GDProductCard);
  if (!customElements.get("gd-marquee"))
    customElements.define("gd-marquee", GDMarquee);
})();
